import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

// Accepted upload formats. Matched by MIME type or, as a fallback, by file extension
// (some browsers report an empty/unreliable MIME type for TIFF).
const ALLOWED_MIME = ['application/pdf', 'image/tiff', 'image/jpeg', 'image/png'];
const ALLOWED_EXT = ['pdf', 'tif', 'tiff', 'jpg', 'jpeg', 'png'];
// Only these can be shown as an <img> thumbnail; others get a file badge.
const PREVIEWABLE_MIME = ['image/jpeg', 'image/png'];

/** @param {File} file */
function fileExtension(file) {
  const dot = file.name.lastIndexOf('.');
  return dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();
}

/** @param {File} file */
function isAllowedFile(file) {
  return ALLOWED_MIME.includes(file.type) || ALLOWED_EXT.includes(fileExtension(file));
}

/**
 * <print-customizer>
 *
 * Drives the print product form: paper + size selection, custom-size live pricing,
 * multi-photo upload, and add-to-cart. Fixed sizes use the product's real variants
 * (quantity = number of photos). The "Custom" size uses a $0.01 helper variant where
 * quantity carries the computed price in cents (hidden/relabeled in the cart).
 */
class PrintCustomizer extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    // ---- Config from data attributes ----
    this.customLabel = this.dataset.customLabel || 'Custom';
    this.sizeUnit = this.dataset.sizeUnit || 'in';
    this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 10;
    this.maxFileBytes = (parseFloat(this.dataset.maxFileMb) || 20) * 1024 * 1024;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.customMin = parseFloat(this.dataset.customMin) || 0;

    try {
      this.customRates = JSON.parse(this.dataset.customRates || '{}');
    } catch (e) {
      this.customRates = {};
    }

    // ---- Variant data ----
    const variantScript = this.querySelector('[data-print-variants]');
    try {
      this.variants = JSON.parse(variantScript?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }
    this.variantMap = new Map();
    for (const v of this.variants) {
      this.variantMap.set(this.#key(v.paper, v.size), v);
    }

    // ---- DOM refs ----
    this.form = this.querySelector('.print-customizer__form');
    this.customFields = this.querySelector('[data-custom-fields]');
    this.customHint = this.querySelector('[data-custom-hint]');
    this.fileInput = this.querySelector('[data-file-input]');
    this.photoList = this.querySelector('[data-photo-list]');
    this.photoHint = this.querySelector('[data-photo-hint]');
    this.unitPriceEl = this.querySelector('[data-unit-price]');
    this.photoCountEl = this.querySelector('[data-photo-count]');
    this.totalPriceEl = this.querySelector('[data-total-price]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.spinner = this.querySelector('.print-customizer__submit-spinner');
    this.errorEl = this.querySelector('[data-error]');
    this.successEl = this.querySelector('[data-success]');
    this.successTextEl = this.querySelector('[data-success-text]');

    // ---- State ----
    /** @type {{file: File, id: string}[]} */
    this.photos = [];

    // ---- Wire up ----
    this.form.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onChange);
    this.querySelector('[data-add-photos]')?.addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', this.#onFilesPicked);

    this.#syncCustomVisibility();
    this.#recompute();
  }

  #key(paper, size) {
    return `${paper ?? ''}||${size ?? ''}`;
  }

  get selectedPaper() {
    return this.querySelector('input[name="print-paper"]:checked')?.value || null;
  }

  get selectedSize() {
    return this.querySelector('input[name="print-size"]:checked')?.value || null;
  }

  get isCustom() {
    return this.selectedSize === this.customLabel;
  }

  get dimensions() {
    const width = parseFloat(this.querySelector('[data-dim="width"]')?.value);
    const height = parseFloat(this.querySelector('[data-dim="height"]')?.value);
    return { width: width > 0 ? width : null, height: height > 0 ? height : null };
  }

  #money(cents) {
    return formatMoney(Math.round(cents), this.moneyFormat, this.currency);
  }

  #onChange = (event) => {
    if (event.target?.name === 'print-size') this.#syncCustomVisibility();
    this.#recompute();
  };

  #syncCustomVisibility() {
    if (!this.customFields) return;
    this.customFields.hidden = !this.isCustom;
  }

  #onFilesPicked = (event) => {
    const picked = Array.from(event.target.files || []);
    for (const file of picked) {
      if (this.photos.length >= this.maxFiles) {
        this.#flashHint(`You can add up to ${this.maxFiles} photos.`);
        break;
      }
      if (!isAllowedFile(file)) {
        this.#flashHint(`${file.name} is not a supported format (PDF, TIFF, JPG, PNG).`);
        continue;
      }
      if (file.size > this.maxFileBytes) {
        this.#flashHint(`${file.name} is larger than the allowed size.`);
        continue;
      }
      this.photos.push({ file, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` });
    }
    // allow re-picking the same file again later
    this.fileInput.value = '';
    this.#renderPhotos();
    this.#recompute();
  };

  #flashHint(message) {
    if (!this.photoHint) return;
    const original = this.photoHint.dataset.original ?? this.photoHint.textContent;
    this.photoHint.dataset.original = original;
    this.photoHint.textContent = message;
    clearTimeout(this._hintTimeout);
    this._hintTimeout = setTimeout(() => {
      this.photoHint.textContent = this.photoHint.dataset.original;
    }, 4000);
  }

  #renderPhotos() {
    this.photoList.innerHTML = '';
    for (const photo of this.photos) {
      const li = document.createElement('li');
      li.className = 'print-customizer__photo';

      if (PREVIEWABLE_MIME.includes(photo.file.type)) {
        const img = document.createElement('img');
        img.alt = photo.file.name;
        img.src = URL.createObjectURL(photo.file);
        img.addEventListener('load', () => URL.revokeObjectURL(img.src), { once: true });
        li.append(img);
      } else {
        const badge = document.createElement('span');
        badge.className = 'print-customizer__photo-badge';
        badge.textContent = (fileExtension(photo.file) || 'file').toUpperCase();
        badge.title = photo.file.name;
        li.append(badge);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'print-customizer__photo-remove';
      remove.setAttribute('aria-label', `Remove ${photo.file.name}`);
      remove.textContent = '×';
      remove.addEventListener('click', () => {
        this.photos = this.photos.filter((p) => p.id !== photo.id);
        this.#renderPhotos();
        this.#recompute();
      });

      li.append(remove);
      this.photoList.append(li);
    }
  }

  /**
   * Returns the current selection's pricing, or null if invalid.
   * @returns {{variantId: number, unitCents: number, quantity: number, totalCents: number, custom: boolean} | null}
   */
  #resolve() {
    const paper = this.selectedPaper;
    const size = this.selectedSize;
    const photoCount = this.photos.length;
    if (!paper || !size || photoCount === 0) return null;

    const variant = this.variantMap.get(this.#key(paper, size));
    if (!variant) return null;

    if (this.isCustom) {
      const { width, height } = this.dimensions;
      if (!width || !height) return null;
      const rate = Number(this.customRates[paper]) || 0;
      if (rate <= 0) return null;
      let unitMajor = width * height * rate;
      if (unitMajor < this.customMin) unitMajor = this.customMin;
      const unitCents = Math.max(1, Math.round(unitMajor * 100));
      return {
        variantId: variant.id,
        unitCents,
        quantity: unitCents * photoCount,
        totalCents: unitCents * photoCount,
        custom: true,
      };
    }

    return {
      variantId: variant.id,
      unitCents: variant.price,
      quantity: photoCount,
      totalCents: variant.price * photoCount,
      custom: false,
    };
  }

  #recompute() {
    const photoCount = this.photos.length;
    this.photoCountEl.textContent = String(photoCount);

    // Custom-size hint
    if (this.isCustom && this.customHint) {
      const { width, height } = this.dimensions;
      const rate = Number(this.customRates[this.selectedPaper]) || 0;
      if (width && height && rate > 0) {
        this.customHint.textContent =
          `${width} × ${height} ${this.sizeUnit} · ${this.#money(Math.max(this.customMin * 100, width * height * rate * 100))} per photo`;
      } else {
        this.customHint.textContent = 'Enter width and height to see the price.';
      }
    }

    const resolved = this.#resolve();
    if (resolved) {
      this.unitPriceEl.textContent = this.#money(resolved.unitCents);
      this.totalPriceEl.textContent = this.#money(resolved.totalCents);
      this.submitBtn.disabled = false;
    } else {
      this.unitPriceEl.textContent = '—';
      this.totalPriceEl.textContent = '—';
      this.submitBtn.disabled = true;
    }
  }

  #setLoading(loading) {
    if (this.spinner) this.spinner.hidden = !loading;
    if (loading) {
      this.submitBtn.disabled = true;
    } else {
      // Let the current selection decide whether the button is enabled.
      this.#recompute();
    }
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  #showSuccess(message) {
    if (!this.successEl) return;
    if (this.successTextEl) this.successTextEl.textContent = message;
    this.successEl.hidden = !message;
    clearTimeout(this._successTimeout);
    if (message) {
      this._successTimeout = setTimeout(() => {
        this.successEl.hidden = true;
      }, 6000);
    }
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    this.#showError('');
    this.#showSuccess('');

    const resolved = this.#resolve();
    if (!resolved) {
      this.#showError('Please choose a paper, size, and add at least one photo.');
      return;
    }

    const formData = new FormData();
    formData.append('id', String(resolved.variantId));
    formData.append('quantity', String(resolved.quantity));
    formData.append('properties[_print]', '1');
    formData.append('properties[Photos]', String(this.photos.length));

    if (resolved.custom) {
      const { width, height } = this.dimensions;
      formData.append('properties[Paper]', this.selectedPaper);
      formData.append('properties[Size]', this.customLabel);
      formData.append('properties[Dimensions]', `${width} × ${height} ${this.sizeUnit}`);
      formData.append('properties[_custom]', '1');
      formData.append('properties[_unit_price]', this.#money(resolved.unitCents));
    }

    this.photos.forEach((photo, index) => {
      formData.append(`properties[Artwork ${index + 1}]`, photo.file, photo.file.name);
    });

    // Ask the server to re-render the cart sections so the drawer can update.
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    this.#setLoading(true);

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      // fetchConfig sets a JSON content-type; remove it so the browser sets the
      // correct multipart boundary for the file upload.
      if (cfg.headers) delete cfg.headers['Content-Type'];

      const response = await fetch(Theme.routes.cart_add_url, {
        ...cfg,
        headers: { ...cfg.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        this.#showError(result.description || result.message || 'Could not add to cart.');
        this.#setLoading(false);
        return;
      }

      // Refresh cart UI + open the drawer, mirroring the theme's product form.
      const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => undefined);
      document.dispatchEvent(
        new CartAddEvent(cart ?? {}, this.id || 'print-customizer', {
          source: 'print-customizer',
          itemCount: this.photos.length,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );

      // Confirmation message (count captured before we clear the photos).
      const addedCount = this.photos.length;
      this.#showSuccess(`Added ${addedCount} ${addedCount === 1 ? 'photo' : 'photos'} to your cart.`);

      // Reset photos for the next order; keep paper/size selection.
      this.photos = [];
      this.#renderPhotos();
      this.#recompute();
    } catch (error) {
      console.error('[print-customizer] add to cart failed', error);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.#setLoading(false);
    }
  };
}

if (!customElements.get('print-customizer')) {
  customElements.define('print-customizer', PrintCustomizer);
}
